/**
 * Встраиваемая панель активности для карточки сущности (лид, партнёр).
 *
 * Использует тот же `useActivityRoot`, что и центральная страница `/messages` —
 * получает полноценный `ActivityController` со всей оркестрацией (markThreadRead,
 * realtime, dispose, loadChat/loadPins). При маунте находит тред сущности через
 * `findByEntity` (с retry на гонку с `onCreate`-хуком) и программно вызывает
 * `controller.selectThread(thread.id)` — дальше работает обычный `ActivityChatPanel`.
 *
 * follow_id не резолвится — показываем тред самой сущности как есть.
 */
import { useEffect, useRef, useState } from 'react';
import { Center, Loader, Text } from '@mantine/core';
import logger from '@ayocore/platform/common/helpers/logger';
import { useActivityThreadRepository } from '@ayocore/activity/client/rpc';
import { useActivityRoot } from '../../hooks/useActivityRoot';
import { ActivityChatPanel } from '../chat/ActivityChatPanel';

type EntityActivityPanelProps = {
  entityType: string;
  entityUuid: string;
};

const RETRY_DELAY_MS = 500;
const MAX_RETRIES = 3;

type LoadStatus = 'loading' | 'ready' | 'empty';

export function EntityActivityPanel({ entityType, entityUuid }: EntityActivityPanelProps) {
  const controller = useActivityRoot({ isModal: true });
  const threadRepository = useActivityThreadRepository();
  const [status, setStatus] = useState<LoadStatus>('loading');
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    setStatus('loading');

    let retries = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const tryFind = async (): Promise<void> => {
      try {
        const thread = await threadRepository.findByEntity(entityType, entityUuid);
        if (cancelledRef.current) return;
        if (thread && typeof thread.id === 'number' && thread.id > 0) {
          // Передаём найденный тред в selectThread как snapshot —
          // он сохранится в selectedThread до того как loadThreads принесёт FeedItem.
          // Без этого был race: selectedThreadId=X, selectedThread=null → sync видел items=0
          // и каскадно сбрасывал всё.
          controller.selectThread(thread.id, thread);
          setStatus('ready');
          return;
        }
        if (retries < MAX_RETRIES) {
          retries += 1;
          retryTimer = setTimeout(() => {
            if (cancelledRef.current) return;
            void tryFind();
          }, RETRY_DELAY_MS);
          return;
        }
        // Ретраи исчерпаны — треда нет (упавший хук, legacy-импорт).
        // Self-healing: просим сервер создать тред (разрешено только для типов
        // с allowEnsure у thread-провайдера; иначе сервер откажет — покажем empty).
        try {
          const ensured = await threadRepository.ensureThreadByEntity(entityType, entityUuid);
          if (cancelledRef.current) return;
          if (ensured && typeof ensured.id === 'number' && ensured.id > 0) {
            controller.selectThread(ensured.id, ensured);
            setStatus('ready');
            return;
          }
        } catch (ensureError) {
          logger.warn('[EntityActivityPanel] ensureThreadByEntity отклонён', {
            entityType,
            entityUuid,
            error: ensureError,
          });
        }
        setStatus('empty');
      } catch (error) {
        if (cancelledRef.current) return;
        logger.error('[EntityActivityPanel] findByEntity failed', {
          entityType,
          entityUuid,
          error,
        });
        setStatus('empty');
      }
    };

    void tryFind();

    return () => {
      cancelledRef.current = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [entityType, entityUuid, controller, threadRepository]);

  if (status === 'loading') {
    return (
      <Center mih={120}>
        <Loader size='sm' />
      </Center>
    );
  }

  if (status === 'empty') {
    return (
      <Center mih={120}>
        <Text size='sm' c='dimmed'>
          Лента активности пока пуста.
        </Text>
      </Center>
    );
  }

  return <ActivityChatPanel controller={controller} />;
}
